// REF LINKING AND POSITION PARSING — the register's two deterministic text derivations.
//
// Nothing here reads the database and nothing here guesses. Both derivations exist because the
// agent writes STRUCTURE INTO PROSE and the schema does not carry it back:
//
//   1. An open question names the rows it is about — "Refine AC-001.1 with the client…" — and
//      `open_questions` has no requirement_id / acceptance_criterion_id column to hold the link
//      (register N1, unbuilt; see FEASIBILITY G-3 for why the agent tool cannot be trusted to
//      supply it either). So the refs are recovered from the text, and every chip is a ROUTE to
//      a row this page already holds — never a claim that the schema stores the link.
//
//   2. A conflict's `context` is one prose blob with three LITERAL keys per side —
//      `Position A:`, `source:`, `quote:`. Those keys are written by the agent's own template,
//      so splitting on them is deterministic. When the match fails the caller falls back to
//      rendering the prose as prose: dressing something as evidence that did not parse as
//      evidence is exactly the lie this product is written against.
//
// Dependency-free (no React, no api module) so it is unit-testable.

/** The product's ref vocabulary. `AC-014.2` and `REQ-014` both match; the `.n` is optional. */
export const REF_RE = /\b(REQ|AC|DEL|TC|DOC|CR)-\d+(?:\.\d+)?\b/g;

/** One ref, classified by the surface that owns it. */
export type RefKind = 'REQ' | 'AC' | 'DEL' | 'TC' | 'DOC' | 'CR';

export const refKind = (ref: string): RefKind | null => {
  const m = /^(REQ|AC|DEL|TC|DOC|CR)-/.exec(ref);
  return m ? (m[1] as RefKind) : null;
};

/**
 * The schema's own derivation rule (DESIGN §4): an acceptance criterion is numbered inside its
 * requirement, so `AC-014.2` belongs to `REQ-014`. Anything else is returned unchanged.
 */
export const parentRef = (ref: string): string =>
  ref.startsWith('AC-') ? `REQ-${ref.slice(3).replace(/\.\d+$/, '')}` : ref;

/** Every distinct ref named in a blob of text, in first-appearance order. */
export function refsIn(...texts: (string | null | undefined)[]): string[] {
  const hay = texts.filter(Boolean).join(' ');
  return [...new Set(hay.match(REF_RE) ?? [])];
}

/** One side of a conflict, as the agent wrote it. */
export interface Position {
  side: string;
  /** The claim this side makes, in the agent's words. */
  claim: string;
  /** The document ref the quote came from. */
  doc: string;
  /** The document's effective date, as the agent printed it. */
  date: string;
  /** The verbatim span. Rendered in full evidence dress, never paraphrased. */
  quote: string;
}

const POSITION_RE =
  /^Position ([AB]):\s*([\s\S]*?)\n\s*source:\s*(\S+)\s*\(([^)]*)\)[^\n]*\n\s*quote:\s*"([\s\S]*?)"/;

/**
 * Split a question's `context` into its positions. Returns [] when the text is not a conflict —
 * the caller then renders the prose as prose.
 */
export function positions(context: string | null | undefined): Position[] {
  if (!context) return [];
  return context
    .split(/\n(?=Position [AB]:)/)
    .map((chunk) => {
      const m = POSITION_RE.exec(chunk);
      if (!m) return null;
      return { side: m[1], claim: m[2].trim(), doc: m[3], date: m[4], quote: m[5] };
    })
    .filter((p): p is Position => p !== null);
}

/** `Hinges on: "…"` — the one line that says what the two sides actually disagree about. */
export function hinge(context: string | null | undefined): string | null {
  const m = /Hinges on:\s*"?([^"\n]+)"?/.exec(context ?? '');
  return m ? m[1].trim() : null;
}

const norm = (t: string | null | undefined): string =>
  String(t ?? '').toLowerCase().replace(/\s+/g, ' ').replace(/[.…]+$/, '').trim();

/**
 * The register row a conflict OPPOSES.
 *
 * The live app carries this link as `proposal.target_requirement_id`; an open question logged
 * FROM a conflict does not carry it, so it is matched on the statement — and the page labels the
 * result "derived, not stored". A short claim is refused outright rather than matched loosely.
 */
export function anchorRow<T extends { ref: string; statement: string | null }>(
  pos: Position[],
  rows: T[],
): { row: T; side: string } | null {
  for (const p of pos) {
    const c = norm(p.claim);
    if (c.length < 12) continue;
    const row = rows.find((r) => {
      const st = norm(r.statement);
      return st.length >= 12 && (st === c || st.startsWith(c) || c.startsWith(st));
    });
    if (row) return { row, side: p.side };
  }
  return null;
}
